import { useEffect, useState, type FormEvent } from 'react';
import { errorText } from '../api';
import { formatDateTimeShort } from '../format';
import { adminApi, type WebhookDelivery, type WebhookEndpoint } from './api';

/**
 * „Anbindungen“ (Konzept 9.1): EverCal meldet jede Buchung, Umbuchung und Absage an eine Zieladresse – gedacht
 * für das Erfolgsmagneten OS. Zieladresse, Geheimnis (einmal angezeigt), Schalter, Testnachricht, letzte
 * Zustellungen. Nur mit Login; ein Schlüssel oder eine KI kommt hier nicht ran.
 */
export function IntegrationsPage() {
  const [endpoint, setEndpoint] = useState<WebhookEndpoint | null | undefined>(undefined);
  const [deliveries, setDeliveries] = useState<WebhookDelivery[]>([]);
  const [url, setUrl] = useState('');
  const [secret, setSecret] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);

  const load = () =>
    adminApi
      .getWebhook()
      .then((r) => {
        setEndpoint(r.endpoint);
        setDeliveries(r.deliveries);
        if (r.endpoint) setUrl(r.endpoint.url);
      })
      .catch((e) => setMessage({ kind: 'error', text: errorText(e) }));
  useEffect(() => void load(), []);

  async function run(fn: () => Promise<void>) {
    setBusy(true);
    setMessage(null);
    try {
      await fn();
    } catch (e) {
      setMessage({ kind: 'error', text: errorText(e) });
    } finally {
      setBusy(false);
    }
  }

  function save(event: FormEvent) {
    event.preventDefault();
    void run(async () => {
      const r = await adminApi.saveWebhook({ url: url.trim(), ...(endpoint ? {} : { active: true }) });
      if (r.secret) {
        setSecret(r.secret);
        setCopied(false);
      }
      setMessage({ kind: 'ok', text: 'Gespeichert.' });
      await load();
    });
  }

  const toggle = (active: boolean) =>
    run(async () => {
      await adminApi.saveWebhook({ active });
      await load();
    });

  const rotate = () => {
    if (!window.confirm('Neues Geheimnis erzeugen? Das bisherige gilt sofort nicht mehr – trag das neue im OS ein.')) return;
    void run(async () => {
      const r = await adminApi.rotateWebhookSecret();
      setSecret(r.secret);
      setCopied(false);
    });
  };

  const test = () =>
    run(async () => {
      const r = await adminApi.testWebhook();
      const d = r.delivery;
      if (d?.status === 'delivered') setMessage({ kind: 'ok', text: `Angekommen – das Ziel hat mit ${d.lastStatusCode} geantwortet.` });
      else setMessage({ kind: 'error', text: `Noch nicht angekommen${d?.lastError ? `: ${d.lastError}` : ''}. EverCal versucht es gleich noch einmal.` });
      await load();
    });

  return (
    <>
      <div className="admin-head">
        <div>
          <h1>Anbindungen</h1>
          <p className="muted">EverCal sagt anderen Programmen Bescheid, sobald jemand bucht, umbucht oder absagt – gedacht für das Erfolgsmagneten OS. Das hat nichts mit „KI verbinden“ zu tun; dort bedient dich ein Assistent, hier schickt EverCal Nachrichten los.</p>
        </div>
      </div>
      <div className="admin-grid">
        <form className="card" onSubmit={save}>
          <h2 className="card__title">Ziel der Nachrichten</h2>
          <div className="field">
            <label htmlFor="hook-url">Zieladresse</label>
            <input id="hook-url" type="url" placeholder="https://os.erfolgsmagneten.com/webhooks/evercal" required value={url} onChange={(e) => setUrl(e.target.value)} />
            <span className="muted small">Nur Adressen mit https://. Dorthin schickt EverCal bei jeder Buchung eine Nachricht.</span>
          </div>
          {endpoint === null && <p className="muted small">Beim Speichern entsteht ein Geheimnis, mit dem das Ziel prüfen kann, dass die Nachricht wirklich von EverCal kommt. Es wird einmal angezeigt.</p>}
          {message && <div className={message.kind === 'ok' ? 'success' : 'error'}>{message.text}</div>}
          <div className="save-bar">
            <button type="submit" className="button button--small" disabled={busy}>
              {endpoint ? 'Adresse speichern' : 'Anbindung einrichten'}
            </button>
            {endpoint && (
              <button type="button" className="button button--secondary button--small" disabled={busy} onClick={() => void test()}>
                Testnachricht schicken
              </button>
            )}
          </div>
        </form>
        <div className="card">
          <h2 className="card__title">Stand</h2>
          {endpoint === undefined && <p className="muted small">Lade …</p>}
          {endpoint === null && <p className="muted small">Noch keine Anbindung. Trag links die Zieladresse ein.</p>}
          {endpoint && (
            <>
              <label className="choice">
                <input type="checkbox" checked={endpoint.active} disabled={busy} onChange={(e) => void toggle(e.target.checked)} />
                <span>
                  <strong>{endpoint.active ? 'Eingeschaltet' : 'Ausgeschaltet'}</strong>{' '}
                  <span className="muted small">– {endpoint.active ? 'Buchungen werden gemeldet.' : 'Es wird nichts geschickt und nichts gesammelt.'}</span>
                </span>
              </label>
              {secret ? (
                <div className="notice">
                  <strong>Dein Geheimnis – kopiere es jetzt ins OS, es wird nicht noch einmal angezeigt:</strong>
                  <div className="row" style={{ marginTop: 8 }}>
                    <code className="keybox" style={{ flex: 1 }}>
                      {secret}
                    </code>
                    <button type="button" className="button button--small" onClick={() => void navigator.clipboard?.writeText(secret).then(() => setCopied(true))}>
                      {copied ? 'Kopiert' : 'Kopieren'}
                    </button>
                    <button type="button" className="link-button" onClick={() => setSecret(null)}>
                      Ausblenden
                    </button>
                  </div>
                </div>
              ) : (
                <p className="muted small">
                  Geheimnis verlegt? <button type="button" className="link-button" disabled={busy} onClick={rotate}>Neues Geheimnis erzeugen</button> – das alte gilt dann nicht mehr.
                </p>
              )}
              <p className="muted small">Eingerichtet am {formatDateTimeShort(endpoint.createdAt)}. Wie das Ziel die Nachrichten prüft, steht in <code>docs/anbindung-os.md</code>.</p>
            </>
          )}
        </div>
        <div className="card card--wide">
          <h2 className="card__title">Letzte Nachrichten</h2>
          {deliveries.length === 0 && <p className="muted small">Noch keine. Sobald jemand bucht, steht die Nachricht hier.</p>}
          <ul className="plain-list plain-list--rows">
            {deliveries.map((d) => (
              <li key={d.id}>
                <div>
                  <strong>{label(d.eventType)}</strong>{' '}
                  {d.status === 'delivered' && <span className="tag tag--ok">angekommen</span>}
                  {d.status === 'queued' && <span className="tag tag--muted">wird zugestellt</span>}
                  {d.status === 'failed' && <span className="tag tag--blocked">nicht angekommen</span>}
                  <div className="small muted">
                    {formatDateTimeShort(d.createdAt)}
                    {d.attempts > 0 && <> · {d.attempts === 1 ? '1 Versuch' : `${d.attempts} Versuche`}</>}
                    {d.lastStatusCode !== null && <> · Antwort {d.lastStatusCode}</>}
                    {d.status === 'queued' && d.nextAttemptAt && <> · nächster Versuch {formatDateTimeShort(d.nextAttemptAt)}</>}
                    {d.lastError && d.status !== 'delivered' && <> · {d.lastError}</>}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </>
  );
}

function label(type: WebhookDelivery['eventType']): string {
  switch (type) {
    case 'booking.created':
      return 'Neue Buchung';
    case 'booking.rescheduled':
      return 'Umgebucht';
    case 'booking.cancelled':
      return 'Abgesagt';
    default:
      return 'Testnachricht';
  }
}
